<template>
  <v-container class="fill-height" v-if="!['admin', 'editor'].includes(currentPermission.privilegeLevel)">
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="5">
        <AccessDeniedCard />
      </v-col>
    </v-row>
  </v-container>
  <v-container v-else fluid>
    <v-row align="center" justify="center" class="mx-0">
      <v-col cols="12">
        <v-breadcrumbs :items="breadcrumbItems" large></v-breadcrumbs>
      </v-col>
    </v-row>
    <v-row class="mx-0">
      <v-col cols="12" xs="12" md="12" lg="12" xl="12">
        <v-card outlined>
          <v-card-title class="text-h6 pb-0 mb-1">
            <v-row justify="center" align="center">
              <v-col>
                Your Business Profile
              </v-col>
              <v-col>
                <p class="pa-0 ma-0 text-right">
                  <v-btn depressed color="primary" @click="onSave" large>Save Changes</v-btn>
                </p>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-subtitle>
            <div class="text-caption text--secondary">
              <div>
                The contact details and links you enter on this form are considered as the general details for your
                business and they will attach to each card you issue.
              </div>
              <div class="mt-2">
                Should you wish to grant permission for any team member to add individual contact details and/or links
                to their issued card, this can be done under the Manage Team section, accessible in the left side menu.
                Select the Edit option under the Action menu to update the permissions for each team member.
              </div>
            </div>
          </v-card-subtitle>
          <v-card-text>
            <v-sheet class="overflow-y-auto" height="55vh" max-height="55vh">
              <v-form ref="form" v-model="valid">
                <div class="text-subtitle-1 mt-5 mb-2 black--text">
                  Your business information
                </div>

                <v-card class="grey lighten-4 my-8 px-5 pt-6 py-3">
                  <v-row justify="space-between" no-gutters>
                    <v-col cols="12" md="6">
                      <TextFieldTitle image="briefcase-icon.svg" title="Business name" />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="name"
                        :rules="nameRules"
                        :validate-on-blur="isFirstSubmissionAttempt"
                      >
                      </v-text-field>

                      <TextFieldTitle image="call-icon.svg" title="General phone number" />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="generalPhoneNumber"
                        :validate-on-blur="isFirstSubmissionAttempt"
                      >
                      </v-text-field>

                      <TextFieldTitle image="email-icon.svg" title="General email" />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="generalEmail"
                        :validate-on-blur="isFirstSubmissionAttempt"
                      >
                      </v-text-field>

                      <TextFieldTitle image="location-icon.svg" title="Business address" />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="businessAddress"
                        :validate-on-blur="isFirstSubmissionAttempt"
                      >
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-card>

                <div class="text-subtitle-1 mt-10 mb-5 black--text">
                  Your business socials
                </div>
                <v-card class="grey lighten-4 my-8 px-5  pt-6 py-3">
                  <v-row no-gutters>
                    <v-col cols="12" md="6">
                      <TextFieldTitle image="website-icon.svg" title="Website" />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="website"
                        class="mb-0"
                        :hint="`e.g. ${urlPrefixes.urlPrefix}www.pappyon.com`"
                        ><template v-slot:prepend-inner>
                          <div class="ml-1 mr-n1 mt-1">{{ urlPrefixes.urlPrefix }}</div>
                        </template></v-text-field
                      >
                      <TextFieldTitle image="facebook-icon.svg" title="Facebook" />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="facebook"
                        placeholder="yourhandle"
                        :hint="`e.g. ${urlPrefixes.facebookUrlPrefix}pappyon`"
                        ><template v-slot:prepend-inner>
                          <div class="ml-1 mr-n1 mt-1">{{ urlPrefixes.facebookUrlPrefix }}</div>
                        </template>
                      </v-text-field>
                      <TextFieldTitle image="instagram-icon.svg" title="Instagram " />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="instagram"
                        placeholder="yourhandle"
                        :hint="`e.g. ${urlPrefixes.instagramUrlPrefix}pappyon`"
                      >
                        <template v-slot:prepend-inner>
                          <div class="ml-1 mr-n1 mt-1">{{ urlPrefixes.instagramUrlPrefix }}</div>
                        </template>
                      </v-text-field>
                      <TextFieldTitle image="twitter-icon.svg" title="Twitter" />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="twitter"
                        placeholder="yourhandle"
                        :hint="`e.g. ${urlPrefixes.twitterUrlPrefix}pappyon`"
                        ><template v-slot:prepend-inner>
                          <div class="ml-1 mr-n1 mt-1">{{ urlPrefixes.twitterUrlPrefix }}</div>
                        </template>
                      </v-text-field>
                      <TextFieldTitle image="linkedin-icon.svg" title="Linkedin " />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="linkedin"
                        placeholder="yourhandle"
                        :hint="`e.g. ${urlPrefixes.linkedinUrlPrefix}pappyon`"
                      >
                        <template v-slot:prepend-inner>
                          <div class="ml-1 mr-n1 mt-1">{{ urlPrefixes.linkedinUrlPrefix }}</div>
                        </template>
                      </v-text-field>
                      <!--Start: Youtube-->
                      <TextFieldTitle image="youtube-icon.svg" title="Youtube" />
                      <v-text-field
                        background-color="white"
                        v-model="youtube"
                        dense
                        outlined
                        placeholder="yourhandle"
                        :hint="`e.g. ${urlPrefixes.youtubeurlPrefix}pappyon`"
                      >
                        <template v-slot:prepend-inner>
                          <div class="ml-1 mr-n1 mt-1">{{ urlPrefixes.youtubeurlPrefix }}</div>
                        </template>
                      </v-text-field>
                      <TextFieldTitle image="podcast-icon.svg" title="Podcast" />
                      <v-text-field background-color="white" v-model="podcast" dense outlined>
                        <template v-slot:prepend-inner>
                          <div class="ml-1 mr-n1 mt-1">{{ urlPrefixes.urlPrefix }}</div>
                        </template></v-text-field
                      >

                      <TextFieldTitle image="pappyon-icon.svg" title="Link with Pappyon" />
                      <v-text-field
                        background-color="white"
                        dense
                        outlined
                        v-model="linkWithPappyon"
                        class="mb-0"
                        :hint="`e.g. ${urlPrefixes.linkWithPappyonUrlPrefix}pappyon`"
                        ><template v-slot:prepend-inner>
                          <div class="ml-1 mr-n1 mt-1">{{ urlPrefixes.linkWithPappyonUrlPrefix }}</div>
                        </template></v-text-field
                      >
                    </v-col>
                  </v-row>
                </v-card>

                <div class="text-subtitle-1 mt-10 mb-5 black--text" v-if="otherDetails.length > 0">
                  Other links
                </div>
                <v-card class="grey lighten-4 my-8 px-5  pt-6 py-3" v-if="otherDetails.length > 0">
                  <v-row no-gutters>
                    <v-col cols="12" md="6">
                      <template v-for="(item, index) in otherDetails">
                        <div :key="`other-link${index}`">
                          <TextFieldTitle
                            v-if="item.fieldId != 'PHONE'"
                            :image="getOtherDetailIcon(item.fieldId)"
                            :title="getOtherDetailDisplayText(item.fieldId)"
                          />
                          <v-text-field
                            background-color="white"
                            v-if="item.fieldId != 'PHONE'"
                            :key="`other-link${index}`"
                            dense
                            v-mask="inputMasks[index]"
                            :placeholder="getOtherDetailInputPlaceholder(item.fieldId)"
                            outlined
                            :hint="getOtherDetailHint(item.fieldId)"
                            class="mt-2"
                            v-model="item.value"
                            append-icon="clear"
                            @click:append="removeFromOtherDetails(index)"
                          >
                            <template v-slot:prepend-inner>
                              <div class="ml-1 mr-n1 mt-1">{{ getOtherDetailPrefix(item.fieldId) }}</div>
                            </template>
                          </v-text-field>
                          <v-row v-else :key="`other-link${index}`" class="mt-2">
                            <v-col cols="12">
                              <TextFieldTitle image="call-icon.svg" title="Phone" />
                            </v-col>

                            <v-col cols="4" class="my-0 py-0">
                              <v-autocomplete
                                v-model="item.selectedTelephoneCode"
                                label="Country"
                                required
                                outlined
                                dense
                                :items="telephoneCodes"
                                item-text="countryCode"
                                item-value="countryCode"
                                background-color="white"
                                return-object
                                :filter="filterTelephoneCodeAutocompleteBox"
                              >
                                <template slot="item" slot-scope="data">
                                  {{ data.item.name }} ({{ data.item.countryCode }})
                                </template>
                              </v-autocomplete>
                            </v-col>
                            <v-col cols="8" class="my-0 py-0">
                              <v-text-field
                                background-color="white"
                                :prefix="item.selectedTelephoneCode.number"
                                placeholder=" "
                                dense
                                label="Phone Number"
                                v-mask="inputMasks[index]"
                                outlined
                                v-model="item.value"
                                append-icon="clear"
                                @click:append="removeFromOtherDetails(index)"
                              >
                              </v-text-field>
                            </v-col>
                          </v-row>
                        </div>
                      </template>
                    </v-col>
                  </v-row>
                </v-card>

                <v-btn @click="presentAddOtherDetailDialog" color="primary" class="mt-2" outlined depressed
                  ><v-icon>add</v-icon>Add Additional</v-btn
                >
                <v-dialog v-model="addOtherDetailDialog" max-width="500">
                  <v-card>
                    <v-card-title>
                      Please Select
                    </v-card-title>
                    <v-card-subtitle class="pb-0">
                      Please select the type of detail you wish to add.
                    </v-card-subtitle>
                    <v-card-text class="pt-0">
                      <v-radio-group v-model="selectedOtherDetailType">
                        <v-radio
                          v-for="(otherDetailType, index) in otherDetailTypes"
                          :key="'OtherDetailTypeRadio' + index"
                          :label="getOtherDetailDisplayText(otherDetailType)"
                          :value="otherDetailType"
                        ></v-radio>
                      </v-radio-group>
                      <v-btn
                        class="mt-2 text-body-1"
                        large
                        elevation="0"
                        color="primary"
                        block
                        @click="addOtherDetails"
                      >
                        Add
                      </v-btn>
                      <v-btn
                        class="mt-2 text-body-1"
                        large
                        text
                        elevation="0"
                        color="primary"
                        block
                        @click="addOtherDetailDialog = false"
                      >
                        Cancel
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-dialog>
              </v-form>
            </v-sheet>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar v-model="snackbar" left multi-line :timeout="snackbarTimeout">
      {{ snackbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn color="primary" text v-bind="attrs" @click="snackbar = false" top>
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import AccessDeniedCard from "@/components/AccessDeniedCard";
import Utility from "@/shared/utility";
import BusinessProfileUtility from "@/shared/business-profile-utility";
import TextFieldTitle from "@/components/TextFieldTitle";

export default {
  name: "ManageBusinessProfile",
  components: {
    AccessDeniedCard,
    TextFieldTitle,
  },
  data() {
    return {
      addOtherDetailDialog: false,
      isFirstSubmissionAttempt: true,
      valid: false,
      nameRules: [(v) => !!v || "This field is required"],
      snackbar: false,
      snackbarText: "",
      snackbarTimeout: 5000,
      breadcrumbItems: [
        // {
        //     text: 'Home',
        //     disabled: false,
        //     to: '/',
        // },
        {
          text: "Your Card",
          disabled: false,
          to: "/manageCard",
        },
        {
          text: "Your Business Profile",
          disabled: true,
        },
      ],
      name: "",
      email: "",
      website: "",
      facebook: "",
      instagram: "",
      twitter: "",
      linkedin: "",
      youtube: "",
      podcast: "",
      otherDetails: [],
      otherDetailTypes: [
        "WEBSITE",
        "LINKEDIN",
        "FACEBOOK",
        "INSTAGRAM",
        "TWITTER",
        "YOUTUBE",
        "EMAIL",
        "PHONE",
        "TOLL_FREE",
        "ADDRESS",
        "OTHER_LINK",
      ],
      selectedOtherDetailType: "",
      generalEmail: "",
      generalPhoneNumber: "",
      businessAddress: "",
      linkWithPappyon: "",
    };
  },
  computed: {
    ...mapGetters([
      "businessProfile",
      "telephoneCodes",
      "countryList",
      "urlPrefixes",
      "currentPermission",
      "linkWithPappyonId",
    ]),
    inputMasks() {
      let inputMasks = [];
      for (let i = 0; i < this.otherDetails.length; i++) {
        if (this.otherDetails[i].fieldId == "TOLL_FREE") {
          inputMasks.push("#### ### ### ###");
        }
        if (this.otherDetails[i].fieldId == "PHONE") {
          if (this.otherDetails[i].selectedTelephoneCode.countryCode == "AU") {
            inputMasks.push("W## ### ######");
          } else {
            inputMasks.push("### ### ######");
          }
        } else {
          inputMasks.push("");
        }
      }
      return inputMasks;
    },
  },
  mounted() {
    this.initEditor();
  },
  created() {
    this.getTelephoneCodeFromCountry = Utility.getTelephoneCodeFromCountry;
    this.getOtherDetailDisplayText = BusinessProfileUtility.getOtherDetailDisplayText;
    this.getOtherDetailPrefix = BusinessProfileUtility.getOtherDetailPrefix;
    this.getOtherDetailPrependIcon = BusinessProfileUtility.getOtherDetailPrependIcon;
    this.getOtherDetailInputPlaceholder = BusinessProfileUtility.getOtherDetailInputPlaceholder;
    this.getOtherDetailHint = BusinessProfileUtility.getOtherDetailHint;
    this.getOtherDetailIcon = BusinessProfileUtility.getOtherDetailIcon;
  },
  watch: {
    facebook: function(newVal, oldVal) {
      if (newVal.startsWith(this.urlPrefixes.facebookUrlPrefix)) {
        this.facebook = this.facebook.replace(this.urlPrefixes.facebookUrlPrefix, "");
      }
    },
    instagram: function(newVal, oldVal) {
      if (newVal.startsWith(this.urlPrefixes.instagramUrlPrefix)) {
        this.instagram = this.instagram.replace(this.urlPrefixes.instagramUrlPrefix, "");
      }
    },
    twitter: function(newVal, oldVal) {
      if (newVal.startsWith(this.urlPrefixes.twitterUrlPrefix)) {
        this.twitter = this.twitter.replace(this.urlPrefixes.twitterUrlPrefix, "");
      }
    },
    linkedin: function(newVal, oldVal) {
      if (newVal.startsWith(this.urlPrefixes.linkedinUrlPrefix)) {
        this.linkedin = this.linkedin.replace(this.urlPrefixes.linkedinUrlPrefix, "");
      }
    },
    youtube: function(newVal, oldVal) {
      if (newVal.startsWith(this.urlPrefixes.youtubeurlPrefix)) {
        this.youtube = this.youtube.replace(this.urlPrefixes.youtubeurlPrefix, "");
      }
    },
    website: function(newVal, oldVal) {
      if (newVal.startsWith(this.urlPrefixes.urlPrefix)) {
        this.website = this.website.replace(this.urlPrefixes.urlPrefix, "");
      }
    },
    podcast: function(newVal, oldVal) {
      if (newVal.startsWith(this.urlPrefixes.urlPrefix)) {
        this.podcast = this.podcast.replace(this.urlPrefixes.urlPrefix, "");
      }
    },
    linkWithPappyon: function(newVal, oldVal) {
      if (newVal.startsWith(this.urlPrefixes.linkWithPappyonUrlPrefix)) {
        this.linkWithPappyon = this.linkWithPappyon.replace(this.urlPrefixes.linkWithPappyonUrlPrefix, "");
      }
    },
    linkWithPappyonId: function(newVal) {
      this.linkWithPappyon = this.businessProfile.linkWithPappyonURL || newVal;
    },
  },
  methods: {
    ...mapActions(["updateBusinessProfileDetails"]),
    filterTelephoneCodeAutocompleteBox(item, queryText, itemText) {
      if (
        item.name.toUpperCase().includes(queryText.toUpperCase()) ||
        itemText.toUpperCase().includes(queryText.toUpperCase())
      )
        return true;
      else return false;
    },
    removeFromOtherDetails(index) {
      this.otherDetails.splice(index, 1);
    },
    initEditor() {
      this.name = this.businessProfile.businessName || "";
      this.website = this.businessProfile.websiteURL || "";
      this.facebook = this.businessProfile.facebookURL || "";
      this.instagram = this.businessProfile.instagramURL || "";
      this.twitter = this.businessProfile.twitterURL || "";
      this.linkedin = this.businessProfile.linkedinURL || "";
      this.generalEmail = this.businessProfile.generalEmail || "";
      this.generalPhoneNumber = this.businessProfile.generalPhoneNumber || "";
      this.businessAddress = this.businessProfile.businessAddress || "";
      this.youtube = this.businessProfile.youtubeURL || "";
      this.podcast = this.businessProfile.podcastURL || "";
      this.linkWithPappyon = this.businessProfile.linkWithPappyonURL || this.linkWithPappyonId;

      this.otherDetails = (this.businessProfile.otherDetails || []).map((element) => {
        return {
          ...element,
          selectedTelephoneCode: {
            countryCode: element.countryPhoneCode || "AU",
            number: element.countryPhoneCodeNumber || "+61",
          },
        };
      });
    },
    async onSave() {
      this.isFirstSubmissionAttempt = false;
      if (this.$refs.form.validate()) {
        this.snackbarText = "Saving changes...";
        this.snackbarTimeout = -1;
        this.snackbar = true;
        try {
          await this.updateBusinessProfileDetails({
            businessName: this.name,
            generalPhoneNumber: this.generalPhoneNumber,
            generalEmail: this.generalEmail,
            businessAddress: this.businessAddress,
            websiteURL: this.website,
            facebookURL: this.facebook,
            instagramURL: this.instagram,
            twitterURL: this.twitter,
            linkedinURL: this.linkedin,
            youtubeURL: this.youtube,
            podcastURL: this.podcast,
            linkWithPappyonURL: this.linkWithPappyon,
            otherDetails: this.otherDetails.map((element) => {
              element.countryPhoneCode = element.selectedTelephoneCode.countryCode;
              element.countryPhoneCodeNumber = element.selectedTelephoneCode.number;
              return element;
            }),
          });
          this.snackbarText = "Changes saved successfully!";
          this.snackbarTimeout = 5000;

          //Start: Update isBusinessCardInformationAdded if required
          if (this.businessProfile.isBusinessCardInformationAdded != true) {
            this.updateBusinessProfileDetails({
              isBusinessCardInformationAdded: true,
            });
          }
          //End: Update isBusinessCardInformationAdded if required
        } catch (error) {
          this.snackbarText = "Changes could not be saved. Please try again later.";
          this.snackbarTimeout = 5000;
          console.log("Error updating the business profile details" + error);
        }
      }
    },
    addOtherDetails() {
      this.otherDetails.push({
        fieldId: this.selectedOtherDetailType,
        value: "",
        index: this.otherDetails.length,
        selectedTelephoneCode: { countryCode: "AU", number: "+61" },
      });
      this.addOtherDetailDialog = false;
    },
    presentAddOtherDetailDialog() {
      this.addOtherDetailDialog = true;
      this.selectedOtherDetailType = this.otherDetailTypes[0];
    },
  },
};
</script>
